import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { parameterValueService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { ParameterValue as ParameterValueType } from "/app/src/models";
import { Search } from "/app/src/components/generic/components/search";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Component for displaying all parameter values in a table
 */
export default function ParameterValues() {
  const { t } = useTranslation();

  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<ParameterValueType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
      }),
      columnHelper.accessor("stationName", {
        id: "stationName",
        cell: (info) => info.getValue(),
        header: t("translation:station"),
      }),
      columnHelper.accessor("value", {
        id: "value",
        cell: (info) => info.getValue(),
        header: t("translation:value"),
      }),
    ],
    [columnHelper, t],
  );

  const {
    data: parameterValues,
    count: parameterValuesCount,
    isFetching,
  } = usePaginatedData<ParameterValueType>({
    queryKey: ["parameter_values"],
    searchString,
    sort,
    page,
    pageSize,
    service: parameterValueService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: parameterValuesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:parameter_values")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={parameterValues}
        tableColumns={columns}
        length={parameterValuesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        emptyText={t("translation:no_parameter_values_found")}
      />
    </div>
  );
}
